import React, { useState } from 'react';
import { stats, reasons, threePaths, footerLinks } from '../mock';
import { ArrowRight, ArrowUpRight, Mail, GraduationCap, UserCheck, Bike, Bolt, CheckCircle2, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { FadeUp, Stagger, StaggerItem, AnimatedCounter } from './MotionReveals';

const footerLinkMap = {
  'Code · 9 thèmes': '/magazine', 'Manœuvres': '/tutos', 'Circulation': '/tutos', 'Trajectoires': '/tutos', 'Vérifications': '/tutos',
  'Magazine': '/magazine', '31 enquêtes': '/magazine', 'Mon livret': '/livret', 'Tutos vidéo': '/tutos', 'FAQ': '/faq',
  'Annuaire des auto-écoles': '/annuaire', 'Livret AM (flipbook)': '/livret-am/lecteur',
  'Élève': '/eleve', 'Enseignant ECSR': '/enseignant',
  'Mentions légales': '/mentions-legales', 'CGU': '/cgu', 'Confidentialité': '/rgpd', 'Cookies': '/cookies', 'Contact': '/a-propos',
  'Signaler une erreur': '/signaler-erreur',
  'Nos plateformes': '/nos-plateformes',
};

export function FutureStudentSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24 grid lg:grid-cols-2 gap-3 sm:gap-4">
      <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-red-950/30 via-[#150a0a] to-[#0d0d0e] p-5 sm:p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-red-600/10 blur-3xl" />
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-3">FUTUR CONDUCTEUR</div>
        <h3 className="font-serif-editorial text-white text-[22px] sm:text-[30px] lg:text-[40px] leading-[1.05]">Réussis ton permis voiture.</h3>
        <p className="text-white/65 text-[13px] sm:text-[14.5px] mt-3 max-w-md">B, AAC, CS, BEA, B96, B+E. Parcours pédagogique en format magazine, sans pression.</p>
        <ul className="mt-4 sm:mt-5 space-y-2 text-white/80 text-[12.5px] sm:text-[14px]">
          {['Parcours guidé par des moniteurs ECSR', '10 scénarios de conduite réelle', '9 dossiers ETG · 31 enquêtes magazine'].map((s, i) => (
            <li key={i} className="flex items-center gap-2"><CheckCircle2 size={15} className="text-red-400 shrink-0" /> {s}</li>
          ))}
        </ul>
        <Link to="/eleve" data-testid="home-eleve-cta" className="mt-5 sm:mt-7 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 shadow-lg shadow-red-900/40">Commencer mon parcours <ArrowRight size={15} /></Link>
      </div>
      <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-8 lg:p-10">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-white/50 font-bold mb-3">JEUNES & SCOLARITÉ</div>
        <h3 className="font-serif-editorial text-white text-[22px] sm:text-[30px] lg:text-[40px] leading-[1.05]">ASSR, BSR, AAC : tu commences ?</h3>
        <p className="text-white/65 text-[13px] sm:text-[14.5px] mt-3 max-w-md">Tu es au collège ou tu prépares ta première voiture ? On t’explique chaque attestation et son rôle dans ton parcours.</p>
        <ul className="mt-4 sm:mt-5 space-y-2 text-white/80 text-[12.5px] sm:text-[14px]">
          {[
            'ASSR 1 (5ᵉ) → ouvre le permis AM (cyclomoteur dès 14 ans)',
            'ASSR 2 (3ᵉ) → obligatoire pour B et conduite accompagnée (AAC)',
            'ASR pour adultes · AER pour établissements spécialisés',
          ].map((s, i) => (
            <li key={i} className="flex items-center gap-2"><CheckCircle2 size={15} className="text-white/70 shrink-0" /> {s}</li>
          ))}
        </ul>
        <Link
          to="/assr"
          data-testid="home-assr-cta"
          className="mt-5 sm:mt-7 h-11 px-5 rounded-full border border-white/15 text-white text-[13px] sm:text-[14px] font-medium hover:bg-white/5 inline-flex items-center gap-2"
        >
          Découvrir le hub ASSR <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}

export function StatsBigSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24 text-center">
      <FadeUp>
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-3">CODE VOITURE</div>
        <h2 className="font-serif-editorial text-white text-[28px] sm:text-[40px] lg:text-[60px] leading-[1.02] sm:leading-[0.96] tracking-tight max-w-4xl mx-auto">La voiture, notre métier. Ta liberté.</h2>
        <p className="mt-3 sm:mt-4 text-white/60 text-[13.5px] sm:text-[15.5px] max-w-2xl mx-auto">Apprends · Progresse · Reste en sécurité. La plateforme pédagogique pensée par des moniteurs voiture agréés ECSR.</p>
      </FadeUp>
      <Stagger className="mt-8 sm:mt-12 grid grid-cols-3 max-w-3xl mx-auto gap-4 sm:gap-6" staggerChildren={0.18}>
        {stats.map((s, i) => (
          <StaggerItem key={i}>
            <AnimatedCounter
              value={s.value}
              duration={1.4 + i * 0.2}
              className="font-serif-editorial text-red-500 text-[36px] sm:text-[56px] lg:text-[80px] leading-none block"
            />
            <div className="text-white/60 text-[11px] sm:text-[13px] mt-2 font-mono-spec uppercase tracking-wider">{s.label}</div>
          </StaggerItem>
        ))}
      </Stagger>
      <div className="mt-10 sm:mt-14">
        <FadeUp>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-3">POURQUOI CODE VOITURE</div>
          <h3 className="font-serif-editorial text-white text-[24px] sm:text-[32px] lg:text-[44px] leading-tight">3 raisons de s’y mettre</h3>
        </FadeUp>
        <Stagger className="mt-6 sm:mt-10 grid md:grid-cols-3 gap-3 sm:gap-4 text-left" staggerChildren={0.12}>
          {reasons.map((r, i) => (
            <StaggerItem key={i} className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-6 hover:border-red-700/40 transition hover:-translate-y-1">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 font-mono-spec font-bold text-[12px] sm:text-[14px]">0{i+1}</div>
              <h4 className="font-serif-editorial text-white text-[18px] sm:text-[22px] mt-3 sm:mt-4">{r.title}</h4>
              <p className="text-white/60 text-[12.5px] sm:text-[14px] mt-2">{r.desc}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

export function MobilitiesSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-20">
      <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-r from-[#0d0d0e] via-[#141416] to-[#0d0d0e] p-5 sm:p-8 lg:p-10 grid lg:grid-cols-[1fr_auto] items-center gap-4 sm:gap-6">
        <div>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2 flex items-center gap-2"><Bike size={13} /> Mobilités urbaines</div>
          <h3 className="font-serif-editorial text-white text-[22px] sm:text-[28px] lg:text-[36px] leading-tight">Tu utilises aussi une trottinette ou un vélo électrique ?</h3>
          <p className="text-white/65 text-[13px] sm:text-[14.5px] mt-3 max-w-2xl">Trottinettes électriques (EDPM) et vélos à assistance électrique (VAE) partagent la route avec les automobilistes. Règles, équipement et assurances — on t’explique tout.</p>
        </div>
        <Link
          to="/magazine/partage-route-velo-trottinette-en-ville"
          data-testid="home-mobilities-cta"
          className="h-11 px-5 rounded-full bg-white text-black text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 hover:bg-white/90 whitespace-nowrap"
        >
          <Bolt size={14} /> Découvrir les mobilités
        </Link>
      </div>
    </section>
  );
}

export function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) { setError('Email invalide'); setStatus('error'); return; }
    setStatus('loading'); setError('');
    try {
      await api.newsletterSubscribe({ email, source: 'footer' });
      setStatus('success');
      setEmail('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur. Réessayez plus tard.');
      setStatus('error');
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-20">
      <div className="rounded-2xl sm:rounded-3xl border border-red-900/30 bg-gradient-to-br from-red-950/30 via-[#150a0a] to-[#0d0d0e] p-5 sm:p-8 lg:p-10">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 sm:gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-red-400 mb-2 sm:mb-3"><Mail size={16} /><span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold">Le baromètre du mois</span></div>
            <h3 className="font-serif-editorial text-white text-[22px] sm:text-[28px] lg:text-[36px] leading-tight">Le baromètre ONISR, chaque mois dans ta boîte.</h3>
            <p className="text-white/60 text-[13px] sm:text-[14.5px] mt-3 max-w-xl">Le décryptage Code Voiture du dernier baromètre mensuel ONISR : tués, blessés, évolutions par usager. <strong>Une fois par mois</strong>, le 1er. Zéro pub, zéro spam.</p>
          </div>
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 lg:max-w-md w-full">
            <input
              data-testid="newsletter-email-input"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
              type="email"
              placeholder="ton@email.fr"
              disabled={status === 'loading' || status === 'success'}
              className="flex-1 h-12 px-4 rounded-full bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500 transition disabled:opacity-60"
            />
            <button
              data-testid="newsletter-submit-btn"
              type="submit"
              disabled={status === 'loading' || status === 'success'}
              className="h-12 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-red-900/40 disabled:opacity-70 whitespace-nowrap"
            >
              {status === 'loading' ? (<><Loader2 size={14} className="animate-spin" /> Inscription…</>) :
               status === 'success' ? 'Inscrit !' :
               <>M'inscrire <ArrowRight size={15} /></>}
            </button>
          </form>
        </div>
        {status === 'error' && <p className="mt-3 text-red-400 text-[13px]" data-testid="newsletter-error">{error}</p>}
        {status === 'success' && <p className="mt-3 text-green-400 text-[13px]" data-testid="newsletter-success">Merci ! Tu recevras le prochain baromètre le 1er du mois prochain.</p>}
        <p className="mt-4 text-white/40 text-[12px]">Désinscription en un clic. Aucune donnée transmise à un tiers. Conformité RGPD totale.</p>
      </div>
    </section>
  );
}

export function ThreePathsSection() {
  const icons = [GraduationCap, UserCheck];
  const routes = ['/eleve', '/enseignant'];
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-20">
      <div className="text-center mb-7 sm:mb-10">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Tu es ?</div>
        <h2 className="font-serif-editorial text-white text-[24px] sm:text-[32px] lg:text-[46px] leading-tight">Deux parcours, un même esprit voiture</h2>
      </div>
      <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
        {threePaths.map((p, i) => {
          const Icon = icons[i];
          return (
            <Link key={i} to={routes[i]} className="group rounded-2xl sm:rounded-3xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7 lg:p-8 hover:border-red-700/40 hover:bg-[#121214] transition-all relative overflow-hidden block">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {p.subtag ? (
                    <>
                      <span className="px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold tracking-wider">{p.tag}</span>
                      <span className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.18em] text-white/50 font-semibold">{p.subtag}</span>
                    </>
                  ) : (
                    <span className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.18em] text-white/50 font-semibold">{p.tag}</span>
                  )}
                </div>
                <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 group-hover:bg-red-600 group-hover:text-white transition">
                  <Icon size={18} />
                </div>
              </div>
              <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] lg:text-[28px] leading-tight mt-5 sm:mt-6">{p.title}</h3>
              <p className="text-white/60 text-[13px] sm:text-[14px] mt-2 sm:mt-3">{p.desc}</p>
              <div className="mt-5 sm:mt-6 inline-flex items-center gap-1.5 text-red-400 text-[12.5px] sm:text-[13px] font-semibold">Accéder <ArrowUpRight size={14} /></div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export function FinalCTA() {
  const { openRegister } = useAuth();
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
      <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a0a0a] via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-8 lg:p-14 grid lg:grid-cols-[1.3fr_1fr] gap-6 sm:gap-10">
        <div>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-3">Code Voiture</div>
          <h2 className="font-serif-editorial text-white text-[26px] sm:text-[36px] lg:text-[56px] leading-[1.02] sm:leading-[0.98] tracking-tight">Le code voiture, façon magazine.</h2>
          <p className="text-white/65 text-[13.5px] sm:text-[15px] mt-4 sm:mt-5 max-w-xl"><strong className="text-white">31 enquêtes</strong> rédigées par des moniteurs ECSR, <strong className="text-white">9 dossiers thématiques</strong> en format magazine éditorial. Tu apprends en lisant — sans chrono, sans score.</p>
          <div className="mt-5 sm:mt-7 flex flex-wrap gap-2 sm:gap-3">
            <Link
              to="/magazine"
              data-testid="home-finalcta-magazine"
              className="h-11 sm:h-12 px-5 sm:px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 shadow-lg shadow-red-900/40"
            >
              Découvrir le magazine <ArrowRight size={15} />
            </Link>
            <button
              type="button"
              data-testid="home-finalcta-register"
              onClick={openRegister}
              className="h-11 sm:h-12 px-5 sm:px-6 rounded-full border border-white/15 text-white text-[13px] sm:text-[14px] font-medium hover:bg-white/5"
            >
              Créer un compte gratuit
            </button>
          </div>
          <p className="text-white/40 text-[11.5px] sm:text-[12.5px] mt-4 sm:mt-5 max-w-xl">Plateforme pédagogique 100 % gratuite. Code Voiture ne vend ni ne réserve aucun passage d’examen officiel — celui-ci s’effectue exclusivement auprès des opérateurs agréés par l’État.</p>
        </div>
        <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0a0a0b] p-4 sm:p-6 lg:p-7">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-white/50 font-bold mb-3 sm:mb-4">Au sommaire</div>
          <ul className="space-y-3 sm:space-y-4">
            {[
              {n: '31', t: 'enquêtes magazine', s: 'Témoignages, statistiques, cas réels'},
              {n: '9', t: 'thèmes officiels', s: 'Magazine éditorial · sans score'},
              {n: 'IA', t: 'Coach IA intégré', s: 'Explication pédagogique sur chaque question'},
            ].map((s, i) => (
              <li key={i} className="flex items-start gap-3 sm:gap-4 pb-3 sm:pb-4 border-b border-white/5 last:border-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-600/10 border border-red-700/30 flex items-center justify-center text-red-400 font-serif-editorial text-[17px] sm:text-[20px] shrink-0">{s.n}</div>
                <div>
                  <div className="text-white text-[13.5px] sm:text-[15px] font-semibold">{s.t}</div>
                  <div className="text-white/55 text-[12px] sm:text-[13px] mt-0.5">{s.s}</div>
                </div>
              </li>
            ))}
          </ul>
          <a href="#login" className="mt-4 sm:mt-5 inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 text-[12.5px] sm:text-[13px] font-semibold">J’ai déjà un compte <ArrowRight size={13} /></a>
        </div>
      </div>
    </section>
  );
}

export function Footer({ minimal = false }) {
  if (minimal) {
    return (
      <footer className="mt-14 sm:mt-24 border-t border-white/5 bg-[#070708]" data-testid="footer-minimal">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <Logo variant="horizontal" height={40} />
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 text-[12px] text-white/40">
            <div>© 2026 CODEVOITURE.FR Tous droits réservés. · <Link to="/mentions-legales" className="underline hover:text-white/70">Mentions légales</Link> · v.20260713</div>
            <div className="flex items-center gap-4 font-mono-spec">
              <span>100 % gratuit</span><span>·</span><span>Sans pub</span><span>·</span><span>Sans tracking</span>
            </div>
          </div>
        </div>
      </footer>
    );
  }
  return (
    <footer className="mt-14 sm:mt-24 border-t border-white/5 bg-[#070708]">
      {/* Pro CTA — Auto-écoles / moniteurs indépendants */}
      <div className="border-b border-white/5 bg-gradient-to-r from-[#100609] via-[#0a0a0b] to-[#0a0d0b]" data-testid="pro-cta-footer">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-5 sm:py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-widest font-mono-spec text-red-400/80 font-bold mb-1">Auto-école · Moniteur indépendant</div>
            <div className="text-white text-[14.5px] sm:text-[16px] font-serif-editorial leading-snug">
              Envie d&apos;équiper ton équipe avec Code Voiture ? <strong className="text-red-300">Parlons-en.</strong>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/demande-info?type=demo" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-white/15 hover:border-white/30 text-white/85 hover:text-white text-[12.5px] font-semibold transition" data-testid="pro-cta-footer-demo">
              Demander une démo
            </Link>
            <Link to="/demande-info?type=partnership" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-semibold transition" data-testid="pro-cta-footer-partnership">
              Équiper mon équipe <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>

      {/* Cross-platform passerelle callout */}
      <div className="border-b border-white/5 bg-gradient-to-r from-[#0a0d1a] via-[#0e0709] to-[#0d0d0e]" data-testid="passerelle-footer">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-5 sm:py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-widest font-mono-spec text-white/45 font-bold mb-1">Écosystème LLB ECSR</div>
            <div className="text-white text-[14.5px] sm:text-[16px] font-serif-editorial leading-snug">
              Vous préparez aussi un <strong className="text-sky-300">permis 2 roues</strong> (BSR/AM, A1, A2, A) ? Rendez-vous sur <span className="text-sky-300">code2roues.fr</span>.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/nos-plateformes" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-white/15 hover:border-white/30 text-white/85 hover:text-white text-[12.5px] font-semibold transition" data-testid="passerelle-in-app">
              Découvrir nos plateformes
            </Link>
            <a href="https://code2roues.fr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-sky-500 hover:bg-sky-400 text-black text-[12.5px] font-semibold transition" data-testid="passerelle-external">
              Ouvrir code2roues.fr <ArrowRight size={13} />
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 sm:py-14">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-1">
            <Logo variant="horizontal" height={48} />
            <p className="mt-5 text-white/50 text-[13px] max-w-xs leading-relaxed">Plateforme pédagogique gratuite, sans pub ni tracking, conçue par des moniteurs ECSR pour le permis B et toutes ses extensions.</p>
          </div>
          {Object.entries(footerLinks).map(([key, items]) => (
            <div key={key}>
              <div className="text-white text-[13px] font-bold uppercase tracking-wider mb-4">{key === 'apprendre' ? 'Apprendre' : key === 'ressources' ? 'Ressources' : key === 'audiences' ? 'Audiences' : 'Légal'}</div>
              <ul className="space-y-2.5">
                {items.map((it, i) => (
                  <li key={i}><Link to={footerLinkMap[it] || "/"} className="text-white/55 hover:text-white text-[13.5px] transition">{it}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ═══ Mentions & avertissements légaux ═══ */}
        <div className="mt-14 pt-8 border-t border-white/5 space-y-4 text-[11.5px] leading-relaxed text-white/40" data-testid="footer-legal-disclaimer">
          <p>
            <strong className="text-white/60">Mission de transfert de savoir, à but non lucratif.</strong>{' '}
            CodeVoiture.fr est une plateforme d&apos;apprentissage et d&apos;entraînement au code de la route et à la conduite (permis B).
            Nous n&apos;organisons, ne vendons et ne réservons aucun passage de l&apos;examen officiel. Celui-ci s&apos;effectue exclusivement
            auprès des opérateurs agréés par l&apos;État (La Poste, SGS, Dekra, Pearson VUE, Bureau Veritas, Code&apos;nGo!).
            Aucun lien commercial ne nous unit à ces opérateurs.
          </p>
          <p>
            Les questions et contenus pédagogiques proposés sur CodeVoiture.fr sont rédigés de manière originale par
            Bruno LLORENS (ECSR diplômé d&apos;État) à partir exclusivement de sources officielles publiquement accessibles
            (Code de la route, REMC, arrêtés ministériels, données ONISR).
          </p>
          <p>
            CodeVoiture.fr n&apos;est pas une auto-école et n&apos;organise aucune formation au permis : c&apos;est une plateforme
            éditoriale indépendante à but non lucratif, éditée par un enseignant de la conduite (ECSR). Aucune affiliation,
            partenariat ni accord avec les éditeurs commerciaux du Code de la route (Codes Rousseau®, ENPC®, Ediser®, Prévent®, etc.).
            Les marques citées restent la propriété de leurs détenteurs respectifs. L&apos;identité complète de l&apos;éditeur figure
            dans les <Link to="/mentions-legales" className="underline hover:text-white/70">mentions légales</Link>.
          </p>
          <p className="text-amber-200/60">
            ⚠️ <strong className="text-amber-200/80">Outil d&apos;entraînement — non officiel.</strong> Contenus pédagogiques rédigés
            par des ECSR et enrichis par IA, contrôlés humainement. Le Code de la route évolue : en cas de doute, référez-vous au
            Code de la route sur{' '}
            <a href="https://www.legifrance.gouv.fr" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-200">Légifrance</a>.
            L&apos;éditeur ne peut être tenu responsable d&apos;une erreur ou d&apos;une évolution législative.{' '}
            <Link to="/mentions-legales" className="underline hover:text-amber-200">Lire l&apos;avertissement complet</Link>.
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-white/40">
          <div>© 2026 CODEVOITURE.FR Tous droits réservés. · <Link to="/mentions-legales" className="underline hover:text-white/70">Mentions légales</Link> · v.20260713</div>
          <div className="flex items-center gap-4 font-mono-spec">
            <span>100 % gratuit</span><span>·</span><span>Sans pub</span><span>·</span><span>Sans tracking</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
